import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import { FOCUS_DELAY_MS, PEEK_DELAY_MS, PEEK_DURATION_MS } from '../constants';

interface Args {
  isActive: boolean;
  onShowOptions?: () => void;
}

function useAiWidget({ isActive, onShowOptions }: Args) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPeeking, setIsPeeking] = useState(false);

  const widgetRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const firstRowRef = useRef<HTMLAnchorElement>(null);
  const suppressFocusExpansionRef = useRef(false);

  const reduceMotion = useReducedMotion();

  const closeAndFocus = useCallback(() => {
    suppressFocusExpansionRef.current = true;
    setIsOpen(false);
    setIsHovered(false);
    setIsPeeking(false);
    fabRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isActive || reduceMotion) return undefined;
    const peekIn = setTimeout(() => setIsPeeking(true), PEEK_DELAY_MS);
    const peekOut = setTimeout(() => setIsPeeking(false), PEEK_DELAY_MS + PEEK_DURATION_MS);
    return () => {
      clearTimeout(peekIn);
      clearTimeout(peekOut);
    };
  }, [isActive, reduceMotion]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      closeAndFocus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeAndFocus, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (reduceMotion) {
      firstRowRef.current?.focus();
      return undefined;
    }
    const timeout = setTimeout(() => firstRowRef.current?.focus(), FOCUS_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [isOpen, reduceMotion]);

  const toggleOpen = useCallback(() => {
    if (!isOpen) onShowOptions?.();
    setIsOpen(!isOpen);
  }, [isOpen, onShowOptions]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleWidgetMouseLeave = useCallback(() => {
    if (isOpen) {
      closeAndFocus();
      return;
    }
    setIsHovered(false);
  }, [closeAndFocus, isOpen]);
  const handleFocus = useCallback(() => {
    if (suppressFocusExpansionRef.current) {
      suppressFocusExpansionRef.current = false;
      return;
    }
    setIsHovered(true);
  }, []);

  return {
    isOpen,
    isExpanded: isOpen ? false : isHovered || isPeeking,
    reduceMotion,
    widgetRef,
    fabRef,
    firstRowRef,
    toggleOpen,
    closeAndFocus,
    handleMouseEnter,
    handleMouseLeave,
    handleWidgetMouseLeave,
    handleFocus,
  };
}

export default useAiWidget;
